import { z } from 'zod';

import { apiClient } from './client';
import { uuidv4 } from '@/utils/uuid';

// Payments API — tenant subset (list own, view, upload proof, initiate Kaspi).

// Зеркалит backend App\Enums\PaymentStatus (5 значений). Без 'cancelled'
// отменённый платёж ронял Zod-парсинг всего списка.
export const PaymentStatusZ = z.enum(['pending', 'paid', 'overdue', 'partial', 'cancelled']);
export type PaymentStatus = z.infer<typeof PaymentStatusZ>;

export const PaymentMethodZ = z.enum(['kaspi', 'cash', 'transfer', 'card']);
export type PaymentMethod = z.infer<typeof PaymentMethodZ>;

export const PaymentProofZ = z.object({
  index: z.number().int().nonnegative().optional(),
  path: z.string(),
  // backend при ошибке подписи S3 может вернуть null -- не роняем парсинг.
  url: z.string().nullable(),
  uploaded_at: z.string(),
  mime: z.string().nullable().optional(),
});
export type PaymentProof = z.infer<typeof PaymentProofZ>;

export const PaymentZ = z.object({
  id: z.number().int(),
  lease_id: z.number().int(),
  period_month: z.number().int().min(1).max(12),
  period_year: z.number().int(),
  amount_expected: z.number(),
  amount_paid: z.number(),
  due_date: z.string(),
  paid_at: z.string().nullable().optional(),
  payment_method: PaymentMethodZ.nullable().optional(),
  payment_proofs: z.array(PaymentProofZ).default([]),
  kaspi_transaction_id: z.string().nullable().optional(),
  status: PaymentStatusZ,
  notes: z.string().nullable().optional(),
});
export type Payment = z.infer<typeof PaymentZ>;

const PaginatedPaymentsZ = z.object({
  data: z.array(PaymentZ),
  meta: z
    .object({
      current_page: z.number().int(),
      last_page: z.number().int(),
      per_page: z.number().int(),
      total: z.number().int(),
    })
    .partial()
    .optional(),
});

const ResourcePaymentZ = z.object({ data: PaymentZ });

// POST /payments/{id}/proofs отдаёт ПЛОСКИЙ { message, proof_path, proof_url, index },
// а НЕ { data: Payment }. Раньше парсили как ResourcePaymentZ -> Zod-ошибка
// «data: Required», хотя файл загружался (после рефреша чек был виден).
const UploadProofResponseZ = z.object({
  message: z.string().optional(),
  proof_path: z.string(),
  proof_url: z.string().nullable().optional(),
  index: z.number().int(),
});
export type UploadProofResponse = z.infer<typeof UploadProofResponseZ>;

export const KaspiInitiateResponseZ = z.object({
  data: z.object({
    qr_image: z.string(),
    deep_link: z.string(),
    amount: z.number(),
    expires_at: z.string().optional(),
  }),
});
export type KaspiInitiateResponse = z.infer<typeof KaspiInitiateResponseZ>['data'];

export interface PaymentListParams {
  lease_id?: number;
  status?: PaymentStatus;
  period_year?: number;
  page?: number;
  per_page?: number;
}

export async function listPayments(params: PaymentListParams = {}): Promise<{ data: Payment[] }> {
  const res = await apiClient.get('/payments', { params });
  return { data: PaginatedPaymentsZ.parse(res.data).data };
}

export async function getPayment(id: number): Promise<Payment> {
  const res = await apiClient.get(`/payments/${id}`);
  return ResourcePaymentZ.parse(res.data).data;
}

export async function uploadPaymentProof(
  id: number,
  form: FormData,
): Promise<UploadProofResponse> {
  const res = await apiClient.post(`/payments/${id}/proofs`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return UploadProofResponseZ.parse(res.data);
}

export async function initiateKaspi(id: number): Promise<KaspiInitiateResponse> {
  const res = await apiClient.post(
    `/payments/${id}/pay`,
    {},
    { headers: { 'Idempotency-Key': uuidv4() } },
  );
  return KaspiInitiateResponseZ.parse(res.data).data;
}

export const paymentsApi = {
  list: listPayments,
  get: getPayment,
  uploadProof: uploadPaymentProof,
  initiateKaspi,
};
